import type { HotelOption, IPolicyOverride, IPolicyViolation, PolicyViolationCode } from '@/lib/allocation/types';

// Booking a hotel OUTSIDE the policy (PO 2026-10-27: «ver cómo saltear la política si el
// usuario tiene permisos… explicar por qué esos no tienen hotel»): the pure part of the
// chooser — what each broken rule says to a person, and the override a reason makes.
// Booking validates the same bounds (`PolicyOverrideDto`: reason 3..500).

export const MIN_REASON = 3;
export const MAX_REASON = 500;

/** The HARD rules booking accepts in an override. */
const CODES: ReadonlySet<PolicyViolationCode> = new Set([
  'MIN_STARS',
  'MAX_STARS',
  'MAX_DISTANCE',
  'MAX_COST_PER_NIGHT',
]);

/** One broken rule in words: the i18n key under `hotelSearch.outOfPolicy.why` + its vars. Pure. */
export function ruleOf(
  offer: HotelOption,
  v: IPolicyViolation,
): { key: string; vars: Record<string, string | number> } {
  if (v.limit == null) return { key: `${v.code}_any`, vars: {} };
  switch (v.code) {
    case 'MIN_STARS':
    case 'MAX_STARS':
      return { key: v.code, vars: { value: offer.stars, limit: v.limit } };
    case 'MAX_DISTANCE':
      return offer.distanceKm > 0
        ? { key: v.code, vars: { value: offer.distanceKm.toFixed(1), limit: v.limit } }
        : { key: 'MAX_DISTANCE_unknown', vars: { limit: v.limit } };
    case 'MAX_COST_PER_NIGHT':
      // The cap means the airline's PRICE (commercial-contract P7) and the offer's rate
      // IS the price now — both in the same money. No price (card unavailable) → the
      // rule in words, without a number nobody can stand behind.
      return offer.ratePerRoomUSD == null
        ? { key: `${v.code}_any`, vars: {} }
        : { key: v.code, vars: { value: offer.ratePerRoomUSD, limit: v.limit } };
  }
}

/** The reason as booking will take it, or null while it is too short / too long. Pure. */
export function reasonOf(text: string): string | null {
  const reason = text.trim();
  return reason.length >= MIN_REASON && reason.length <= MAX_REASON ? reason : null;
}

/** The override for an out-of-policy offer chosen with `reason` — null when it cannot be one. Pure. */
export function overrideOf(offer: HotelOption, text: string): IPolicyOverride | null {
  const reason = reasonOf(text);
  const codes = [...new Set((offer.violations ?? []).map((v) => v.code))].filter((c) => CODES.has(c));
  return reason && codes.length > 0 ? { reason, codes } : null;
}
